import * as React from 'react';

import * as Sentry from '@sentry/browser';
import { QueryClient } from '@tanstack/react-query';
import { translate } from '~l10n/client';
import * as moment from 'moment';
import { Provider } from 'react-redux';

import { Toaster } from '~shared/components/Toast';

import store from './src/store';
import { beforeSend } from './src/util/sentry/beforeSend';
import { denyUrls } from './src/util/sentry/denyUrls';
import { ignoreErrors } from './src/util/sentry/ignoreErrors';
import {
  isIgnoredErrorMessage,
  isRateLimitedErrorMessage,
} from './src/util/sentry/sentryHandling';

const popup = require('../shared/components/popup/PopupController');
const user = require('../../shared/models/user');

Sentry.init({
  dsn: window.APP_CONFIG.SENTRY_DSN,
  release: window.APP_CONFIG.VERSION,
  environment: window.APP_CONFIG.SENTRY_ENVIRONMENT,
  // Reference: https://docs.sentry.io/platforms/javascript/configuration/filtering/
  ignoreErrors: ignoreErrors,
  denyUrls: denyUrls,
  beforeSend: beforeSend,
});

interface Props {
  user: any;
}
export class AppView extends React.Component<Props> {
  QueryClient: QueryClient = null;
  constructor(props) {
    super(props);
    const user = props.user;

    Sentry.withScope((scope) => {
      try {
        const id = user.isTeacher()
          ? user.getId()
          : user.getRoomName().toLowerCase();
        scope.setUser({ id: id.toString() });
        scope.setTag('role', user.isTeacher() ? 'teacher' : 'student');
      } catch (err) {
        console.error(err);
      }
    });

    this.QueryClient = new QueryClient();
  }

  componentDidMount() {
    window.onerror = this.handleAppError;
    window.addEventListener('unhandledrejection', function (event) {
      console.error(event);
      event.stopPropagation();
    });
  }
  componentDidCatch(error, errorInfo) {
    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo);
      Sentry.captureException(error);
    });
  }

  handleAppError(message, source, lineno, colno, appError) {
    let sentryEventId = null;
    let displayErrorToUser = true;

    // Log error in sentry
    try {
      if (appError) {
        sentryEventId = Sentry.captureException(appError);
        if (
          appError.stack &&
          appError.stack.includes('mathlive.bundle.min.js')
        ) {
          displayErrorToUser = false;
        }
      } else if (message) {
        const isMessageIgnored = isIgnoredErrorMessage(message);
        if (!isMessageIgnored) {
          sentryEventId = Sentry.captureException(message);
        }

        if (isMessageIgnored || isRateLimitedErrorMessage(message)) {
          displayErrorToUser = false;
        }
      }
    } catch (err) {
      console.error(err);
    }

    if (!displayErrorToUser) {
      return true;
    }

    try {
      const whitelistErrorUrlRegex = RegExp('(socrative|showbie)+', 'g');
      // Check if error comes from a whitelisted source
      if (source && source.length && whitelistErrorUrlRegex.test(source)) {
        // Display dismissible popup to user
        popup.closePopup();

        const buildErrorDetails = ({
          message,
          sentryEventId,
          isStudent = false,
        }: {
          message: string;
          sentryEventId: string;
          isStudent?: boolean;
        }) => {
          sentryEventId = sentryEventId || 'Unknown';
          let errorMessage = 'Could not get error details';
          try {
            errorMessage = message.toString();
          } catch (err) {
            console.error(err);
          }

          return (
            <div className={'popup-error-details'}>
              <strong>
                {translate('Error Details')}
                <br />
              </strong>
              <span>
                {isStudent
                  ? translate('Please provide this info to your teacher')
                  : translate('Please provide this info to our support team')}
              </span>
              <p>[Event ID]: {sentryEventId}</p>
              <p>[App Version]: {window.APP_CONFIG.VERSION}</p>
              <p>[Timestamp]: {moment.utc().toString()}</p>
              <p>[Error]: {errorMessage}</p>
            </div>
          );
        };

        if (user.isTeacher()) {
          popup.render({
            title: translate('Hmm, something unexpected happened.'),
            message: [
              translate(
                `Please refresh and try again. If problems persist, don’t hesitate to contact us for help!`
              ),
              buildErrorDetails({
                message: message,
                sentryEventId: sentryEventId,
              }),
            ],
            buttonText: translate('Refresh'),
            buttonClicked: () => {
              window.location.reload();
            },
            cancelText: translate('Logout'),
            cancelClicked: (closeXClicked: any) => {
              if (closeXClicked) {
                return;
              }

              user.logout(() => {
                window.location.href = '/login/teacher/';
              });
            },
            isModal: true,
          });
        } else {
          popup.render({
            title: translate('Hmm, something unexpected happened.'),
            message: [
              translate(
                `Your answer may not have been saved. Refreshing may help, but you may also lose your answer.`
              ),
              buildErrorDetails({
                message: appError,
                sentryEventId: sentryEventId,
                isStudent: true,
              }),
            ],
            buttonText: translate('Try Again'),
            cancelText: translate('Refresh'),
            cancelClicked: (closeXClicked: any) => {
              if (closeXClicked) {
                return;
              }

              window.location.reload();
            },
            isModal: true,
          });
        }
      }
    } catch (err) {
      console.error(err);
    }

    return true;
  }

  render() {
    return (
      <Provider store={store}>
        <Toaster />

        <div
          id="main-content"
          style={{
            minHeight: window.innerWidth < 768 ? 'calc(100vh - 67px)' : '0',
          }}
        />
      </Provider>
    );
  }
}
